import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractApiErrorMessage } from "../../../utils/apiError";
import { downloadFile } from "../../../utils/downloadUtils";

// Initial state
interface AdminState {
  status: any;
  applications: {
    items: any[];
  };
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  currentPage: number;
  perPage: number;
  totalItems: number;
  loading: boolean;
  error: string | null; // Refined error type to string | null
  documents: any[]; // Add documents property
  applicationDetails: any; // Add applicationDetails property
  reimbursements: any; // Separate reimbursement data
  reimbursementError: string | null;
}

const initialState: AdminState = {
  status: null,
  applications: { items: [] },
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  loading: false,
  error: null,
  meta: {
    pageNumber: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  },
  documents: [], // Initialize documents
  applicationDetails: null, // Initialize applicationDetails
  reimbursements: null, // Initialize reimbursements
  reimbursementError: null,
};
// Async thunk for fetching admin status
export const fetchAdminStatus = createAsyncThunk(
  "admin/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `${endpoints.citizenship.admin.getStatus}?applType=AT011`
      );
      return response.data;
    } catch (error) {
   
      return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch admin status"));
    }
  }
);

// Async thunk for fetching admin applications
export const fetchAdminApplications = createAsyncThunk(
  "admin/fetchApplications",
  async (
    {
      page,
      perPage,
      status,
    }: { page: number; perPage: number; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        endpoints.citizenship.admin.getApplications,
        {
          params: {
            page,
            per_page: perPage,
            status,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        (error as any)?.response
          ? extractApiErrorMessage(error, "Failed to fetch admin applications")
          : "Failed to fetch admin applications"
      );
    }
  }
);

// Async thunk for fetching documents list
export const getDocumentsList = createAsyncThunk(
  "admin/getDocumentsList",
  async (
    { applicationId, nuid }: { applicationId: string; nuid: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        endpoints.citizenship.admin.getDocumentsList(applicationId),
        {
          headers: {
            "x-emp-nuid": nuid,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        // @ts-ignore
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to fetch documents list");
    }
  }
);

// Async thunk for fetching application details
export const getApplicationDetails = createAsyncThunk(
  "admin/getApplicationDetails",
  async (
    { applicationId, nuid }: { applicationId: string; nuid: string },
    { rejectWithValue }
  ) => {
    try {
      console.debug("getApplicationDetails called", { applicationId, nuid });
      const response = await api.get(
        endpoints.citizenship.admin.getApplicationDetails(applicationId),
        {
          headers: {
            "x-emp-nuid": nuid,
          },
        }
      );
      // eslint-disable-next-line no-console
      console.debug("getApplicationDetails response status", response?.status);
      return response.data;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        // @ts-ignore
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to fetch application details");
    }
  }
);

// Async thunk for updating application status
export const updateApplicationStatus = createAsyncThunk(
  "admin/updateApplicationStatus",
  async (
    payload: { applicationIds: string[]; status: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        endpoints.citizenship.admin.updateApplicationStatus,
        payload
      );
      return response.data;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        // @ts-ignore
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to update application status");
    }
  }
);
export const downloadDocument = createAsyncThunk(
  "admin/downloadDocument",
  async (
    {
      applicationId,
      documentId,
      fileName,
      nuid,
    }: {
      applicationId: string;
      documentId: string;
      fileName?: string;
      nuid: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const url = endpoints.citizenship.admin.downloadDocument(
        applicationId,
        documentId
      );
      const response = await api.get(url, {
        responseType: "blob",
        headers: {
          "x-emp-nuid": nuid,
        },
      });

      downloadFile({
        response,
        documentId,
        fileName,
        defaultFileName: "document"
      });

      return { applicationId, documentId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to download document");
    }
  }
);

// Async thunk for fetching reimbursement info
export const getReimbursmentInfo = createAsyncThunk(
  "admin/getReimbursmentInfo",
  async ({ applicationId }: { applicationId: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.citizenship.admin.reimbursements(applicationId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch reimbursement info"));
    }
  }
);

// Async thunk for patching reimbursement info
export const patchReimbursementInfo = createAsyncThunk(
  "admin/patchReimbursementInfo",
  async (
    {
      applicationId,
      data,
      nuid,
    }: { applicationId: string; data: any; nuid: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(
        endpoints.citizenship.admin.reimbursements(applicationId),
        data,
        {
          headers: {
            "x-emp-nuid": nuid,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        // @ts-ignore
        return rejectWithValue((error as any).response.data);
      }
      return rejectWithValue("Failed to patch reimbursement info");
    }
  }
);

// Slice
const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    clearApplications(state) {
      state.applications = { items: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
      })
      .addCase(fetchAdminStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAdminApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminApplications.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        // Handle API response with meta object and items array
        if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
          state.applications = { items: payload.items };
          state.totalItems = payload.meta.total || 0;
          state.meta = {
            pageNumber: payload.meta.pageNumber || 1,
            pageSize: payload.meta.pageSize || 10,
            total: payload.meta.total || 0,
            totalPages: payload.meta.totalPages || 0,
          };
        } else {
          // Fallback for legacy response format
          state.applications = action.payload || { items: [], totalItems: 0 };
          state.totalItems = action.payload?.totalItems || 0;
        }
      })

      .addCase(fetchAdminApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getDocumentsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentsList.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(getDocumentsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getApplicationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getApplicationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.applicationDetails = action.payload;
      })
      .addCase(getApplicationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateApplicationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateApplicationStatus.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateApplicationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(downloadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadDocument.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getReimbursmentInfo.pending, (state) => {
        state.loading = true;
        state.reimbursementError = null;
      })
      .addCase(getReimbursmentInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.reimbursements = action.payload;
      })
      .addCase(getReimbursmentInfo.rejected, (state, action) => {
        state.loading = false;
        state.reimbursementError = action.payload as string;
      })
      .addCase(patchReimbursementInfo.pending, (state) => {
        state.loading = true;
        state.reimbursementError = null;
      })
      .addCase(patchReimbursementInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.reimbursements = action.payload;
      })
      .addCase(patchReimbursementInfo.rejected, (state, action) => {
        state.loading = false;
        state.reimbursementError = action.payload as string;
      });
  },
});
export const { setCurrentPage, clearApplications } = adminSlice.actions;
export default adminSlice.reducer;